import React from 'react';
import { motion } from 'motion/react';
import { Search, Mic, MessageSquare, MousePointer2, Zap, LayoutPanelLeft } from 'lucide-react';
import type { PreviewType } from '@/data/onboardingSteps';

interface PreviewProps {
  previewType: PreviewType;
  isZh: boolean;
}

const ui = (zh: string, en: string, isZh: boolean) => isZh ? zh : en;

/* ── 子元素 stagger 动画辅助 ── */
const staggerItem = (index: number) => ({
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.08 + index * 0.05, duration: 0.25 },
});

export function OnboardingPreview({ previewType, isZh }: PreviewProps) {
  if (previewType === 'desktop') {
    return (
      <div className="relative h-full min-h-[280px] overflow-hidden rounded-2xl border border-white/10 bg-celestial-deep/60 p-5">
        <motion.div {...staggerItem(0)} className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.22em] text-white/35">
          <span>小枫 Desktop</span><span>09:41</span>
        </motion.div>
        <motion.div {...staggerItem(1)} className="mt-7 grid grid-cols-3 gap-4">
          {['Chat', 'Skills', 'Knowledge', 'Run Log', 'Settings', 'Tools'].map((label, index) => (
            <motion.div key={label}
              animate={index === 0 ? { scale: [1, 1.04, 1] } : {}}
              transition={{ repeat: Infinity, duration: 1.8 }}
              className={`flex h-20 flex-col items-center justify-center gap-2 rounded-xl border ${index === 0 ? 'border-celestial-saturn/70 bg-celestial-saturn/15 shadow-[0_0_28px_rgba(255,204,92,0.18)]' : 'border-white/10 bg-white/[0.04]'}`}>
              <div className={`h-7 w-7 rounded-lg ${index === 0 ? 'bg-celestial-saturn' : 'bg-white/15'}`} />
              <span className="text-xs font-semibold text-white/70">{label}</span>
            </motion.div>
          ))}
        </motion.div>
        <motion.div {...staggerItem(2)} className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2 rounded-2xl border border-white/10 bg-black/35 px-4 py-3 backdrop-blur-xl">
          {[0, 1, 2, 3].map(item => <div key={item} className="h-8 w-8 rounded-lg bg-white/12" />)}
        </motion.div>
      </div>
    );
  }

  if (previewType === 'chat') {
    return (
      <div className="flex h-full min-h-[280px] flex-col justify-between rounded-2xl border border-white/10 bg-celestial-deep/60 p-5">
        <div className="space-y-4">
          <motion.div {...staggerItem(0)} className="max-w-[78%] rounded-2xl rounded-tl-sm bg-white/10 px-4 py-3 text-sm text-white/72">
            {ui('帮我打开浏览器，并整理今天要处理的文件。', 'Open the browser and organize the files I need to handle today.', isZh)}
          </motion.div>
          <motion.div {...staggerItem(1)} className="ml-auto max-w-[82%] rounded-2xl rounded-tr-sm border border-celestial-glow/25 bg-celestial-glow/12 px-4 py-3 text-sm text-white/90">
            {ui('我会先确认执行模式，然后打开浏览器和文件入口。', 'I will confirm the execution mode first, then open the browser and file entry.', isZh)}
          </motion.div>
        </div>
        <motion.div {...staggerItem(2)} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/28 p-3">
          <div className="flex-1 rounded-xl bg-white/8 px-4 py-3 text-sm text-white/35">{ui('输入任务...', 'Type a task...', isZh)}</div>
          <button className="flex h-11 w-11 items-center justify-center rounded-xl bg-celestial-glow text-black"><Mic size={18} /></button>
        </motion.div>
      </div>
    );
  }

  if (previewType === 'mode') {
    return (
      <div className="h-full min-h-[280px] rounded-2xl border border-white/10 bg-celestial-deep/60 p-5">
        <motion.div {...staggerItem(0)} className="grid grid-cols-3 gap-2 rounded-2xl border border-white/10 bg-black/25 p-2">
          {[
            [ui('聊天', 'Chat', isZh), ui('只交流', 'Talk only', isZh), MessageSquare],
            [ui('助手', 'Assistant', isZh), ui('调用能力', 'Use abilities', isZh), MousePointer2],
            [ui('自主', 'Autonomy', isZh), ui('多步执行', 'Multi-step', isZh), Zap],
          ].map(([label, hint, Icon], index) => {
            const ActiveIcon = Icon as typeof MousePointer2;
            return (
              <div key={label as string} className={`rounded-xl border px-3 py-4 text-center ${index === 0 ? 'border-celestial-nebula/60 bg-celestial-nebula/15 text-white/95' : 'border-white/8 bg-white/[0.03] text-white/45'}`}>
                <ActiveIcon size={20} className="mx-auto mb-2" />
                <div className="text-sm font-black">{label as string}</div>
                <div className="mt-1 text-[11px] font-semibold">{hint as string}</div>
              </div>
            );
          })}
        </motion.div>
        <motion.div {...staggerItem(1)} className="mt-5 rounded-2xl border border-celestial-nebula/20 bg-celestial-nebula/10 p-4">
          <div className="text-sm font-black text-white/90">{ui('当前：助手模式', 'Current: Assistant Mode', isZh)}</div>
          <p className="mt-2 text-sm leading-relaxed text-white/68">
            {ui(
              '适合按任务选择工具、技能、团队、运行日志或键鼠操作。需要动手前，小枫 会显示正在做什么。',
              '小枫 can choose tools, skills, teams, run logs, or desktop control for the task. Before acting, 小枫 shows what is happening.',
              isZh
            )}
          </p>
        </motion.div>
      </div>
    );
  }

  /* ── 窗口管理预览 ── */
  return (
    <div className="h-full min-h-[280px] rounded-2xl border border-white/10 bg-celestial-deep/60 p-5">
      <motion.div {...staggerItem(0)} className="relative h-[200px] overflow-hidden rounded-xl border border-white/10 bg-black/30">
        {/* 模拟桌面窗口 */}
        <motion.div {...staggerItem(1)} className="absolute top-4 left-4 w-[55%] h-[70%] rounded-lg border border-celestial-nebula/30 bg-celestial-deep/80 shadow-lg overflow-hidden"
          animate={{ x: [0, 3, 0] }} transition={{ repeat: Infinity, duration: 3 }}>
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-white/5">
            <span className="w-2 h-2 rounded-full bg-red-400/60" />
            <span className="w-2 h-2 rounded-full bg-celestial-saturn/60" />
            <span className="w-2 h-2 rounded-full bg-green-400/60" />
            <span className="ml-2 text-[10px] font-bold text-white/50">{ui('聊天', 'Chat')}</span>
          </div>
          <div className="p-3 space-y-2">
            <div className="h-2 w-3/4 rounded bg-white/10" />
            <div className="h-2 w-1/2 rounded bg-white/8" />
          </div>
        </motion.div>
        <motion.div {...staggerItem(2)} className="absolute top-10 right-4 w-[38%] h-[50%] rounded-lg border border-celestial-glow/25 bg-celestial-deep/70 shadow-lg"
          animate={{ x: [0, -2, 0] }} transition={{ repeat: Infinity, duration: 3.5 }}>
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-white/5">
            <span className="w-2 h-2 rounded-full bg-red-400/60" />
            <span className="w-2 h-2 rounded-full bg-celestial-saturn/60" />
            <span className="w-2 h-2 rounded-full bg-green-400/60" />
            <span className="ml-2 text-[10px] font-bold text-white/50">{ui('知识库', 'KB')}</span>
          </div>
          <div className="p-3 space-y-2">
            <div className="h-2 w-2/3 rounded bg-white/10" />
            <div className="h-2 w-1/3 rounded bg-white/8" />
          </div>
        </motion.div>
        {/* Dock */}
        <motion.div {...staggerItem(3)} className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 rounded-xl border border-white/10 bg-black/40 px-3 py-1.5 backdrop-blur">
          {[<MessageSquare key="c" size={14} />, <LayoutPanelLeft key="k" size={14} />, <Search key="s" size={14} />].map((icon, i) => (
            <div key={i} className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/15 text-white/50">{icon}</div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
}
